import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';

const CallContext = createContext(null);

// status: 'idle' | 'outgoing' | 'incoming' | 'in_call'
const initialState = { status: 'idle', call: null };

// Cuanto se ve el aviso ("No contesto", "Esta en otra llamada") antes de cerrar
const AVISO_MS = 2200;

const ERRORES = {
    already_in_call: 'Ya tienes una llamada en curso',
};

export const CallProvider = ({ children }) => {
    const { user } = useAuth();
    const { socket, connected } = useSocket();
    const [state, setState] = useState(initialState);
    // Motivo por el que termino la llamada saliente, visible un momento antes de cerrar
    const [aviso, setAviso] = useState(null);
    const stateRef = useRef(state);
    const avisoRef = useRef(false);
    const avisoTimer = useRef(null);
    const userIdRef = useRef(null);

    useEffect(() => { userIdRef.current = user?.id ?? null; }, [user?.id]);

    // El ref se actualiza en el acto: el siguiente evento del socket puede llegar
    // antes del render
    const poner = useCallback((next) => {
        stateRef.current = next;
        setState(next);
    }, []);

    const reset = useCallback(() => {
        clearTimeout(avisoTimer.current);
        avisoTimer.current = null;
        avisoRef.current = false;
        poner(initialState);
        setAviso(null);
    }, [poner]);

    // Del lado de quien llama: deja el motivo en pantalla un momento y cierra
    const cerrarConAviso = useCallback((texto) => {
        if (stateRef.current.status !== 'outgoing') {
            reset();
            return;
        }
        avisoRef.current = true;
        setAviso(texto);
        clearTimeout(avisoTimer.current);
        avisoTimer.current = setTimeout(reset, AVISO_MS);
    }, [reset]);

    useEffect(() => () => clearTimeout(avisoTimer.current), []);

    // Listeners de signaling
    useEffect(() => {
        if (!socket || !connected) return;

        const esMia = (payload) => {
            const current = stateRef.current.call;
            return !!payload?.room_uuid && current?.room_uuid === payload.room_uuid;
        };

        const onIncoming = (payload) => {
            // Si ya estamos en otra llamada, ignorar (el server ya nos marca busy)
            if (stateRef.current.status !== 'idle') return;
            poner({ status: 'incoming', call: payload });
        };

        const onStarted = (payload) => {
            // Si colgo antes de que el server confirmara, la llamada ya creada se cancela
            const { status, call } = stateRef.current;
            if (status !== 'outgoing' || call?.room_uuid) {
                if (payload?.room_uuid) socket.emit('call_cancel', { room_uuid: payload.room_uuid });
                return;
            }
            poner({ status: 'outgoing', call: payload });
        };

        const onAccepted = (payload) => {
            if (!esMia(payload)) return;
            const { status, call } = stateRef.current;
            if (status === 'outgoing' || status === 'incoming') {
                // jitsi_jwt: token de esta sala para este usuario (Jitsi no deja entrar sin el)
                poner({ status: 'in_call', call: { ...call, jitsi_jwt: payload.jitsi_jwt } });
            }
        };

        const onRejected = (payload) => {
            // 'busy': el callee esta en otra llamada; ni se llego a crear la llamada
            if (payload?.reason === 'busy') {
                const { status, call } = stateRef.current;
                if (status === 'outgoing' && !call?.room_uuid) cerrarConAviso('Está en otra llamada');
                return;
            }
            if (esMia(payload)) cerrarConAviso('Llamada rechazada');
        };

        // Tambien llega cuando se contesto en otro dispositivo del mismo usuario
        const onCancelled = (payload) => {
            if (esMia(payload)) reset();
        };

        const onTimeout = (payload) => {
            if (esMia(payload)) cerrarConAviso('No contestó');
        };

        const onEnded = (payload) => {
            if (esMia(payload)) cerrarConAviso('No contestó');
        };

        const onError = (payload) => {
            console.warn('[Call] error del servidor:', payload?.reason);
            cerrarConAviso(ERRORES[payload?.reason] || 'No se pudo hacer la llamada');
        };

        socket.on('call_incoming', onIncoming);
        socket.on('call_started', onStarted);
        socket.on('call_accepted', onAccepted);
        socket.on('call_rejected', onRejected);
        socket.on('call_cancelled', onCancelled);
        socket.on('call_timeout', onTimeout);
        socket.on('call_ended', onEnded);
        socket.on('call_error', onError);

        // Al (re)conectar se le pregunta al server que llamadas siguen vivas: vuelve a
        // sonar una que entro mientras no habia conexion (PC que desperto, red que volvio)
        // y se limpia la que termino en ese tiempo.
        socket.emit('call_sync', {}, (resp) => {
            const calls = resp?.calls || [];
            const { status, call } = stateRef.current;
            if (status === 'idle') {
                const entrante = calls.find(
                    (c) => c.status === 'ringing' && c.callee_id === userIdRef.current
                );
                if (entrante) poner({ status: 'incoming', call: entrante });
                return;
            }
            if (!call?.room_uuid || avisoRef.current) return;
            const viva = calls.find((c) => c.room_uuid === call.room_uuid);
            if (!viva) reset();
            else if (viva.status === 'accepted' && status === 'outgoing') {
                poner({ status: 'in_call', call: { ...call, jitsi_jwt: viva.jitsi_jwt } });
            }
        });

        return () => {
            socket.off('call_incoming', onIncoming);
            socket.off('call_started', onStarted);
            socket.off('call_accepted', onAccepted);
            socket.off('call_rejected', onRejected);
            socket.off('call_cancelled', onCancelled);
            socket.off('call_timeout', onTimeout);
            socket.off('call_ended', onEnded);
            socket.off('call_error', onError);
        };
    }, [socket, connected, reset, cerrarConAviso, poner]);

    // === Acciones ===
    const startCall = useCallback((conversationId, callType = 'video') => {
        if (!socket || stateRef.current.status !== 'idle') return;
        // Estado provisional mientras llega 'call_started'
        poner({
            status: 'outgoing',
            call: { conversation_id: conversationId, call_type: callType, room_uuid: null },
        });
        socket.emit('call_start', { conversation_id: conversationId, type: callType });
    }, [socket, poner]);

    const acceptCall = useCallback(() => {
        if (!socket) return;
        const current = stateRef.current.call;
        if (!current?.room_uuid) return;
        socket.emit('call_accept', { room_uuid: current.room_uuid });
    }, [socket]);

    const rejectCall = useCallback(() => {
        if (!socket) return;
        const current = stateRef.current.call;
        if (!current?.room_uuid) return;
        socket.emit('call_reject', { room_uuid: current.room_uuid });
        reset();
    }, [socket, reset]);

    const cancelCall = useCallback(() => {
        const current = stateRef.current.call;
        if (socket && current?.room_uuid && !avisoRef.current) {
            socket.emit('call_cancel', { room_uuid: current.room_uuid });
        }
        reset();
    }, [socket, reset]);

    const leaveCall = useCallback(() => {
        if (!socket) {
            reset();
            return;
        }
        const current = stateRef.current.call;
        if (current?.room_uuid) {
            socket.emit('call_leave', { room_uuid: current.room_uuid });
        }
        reset();
    }, [socket, reset]);

    const reportJoined = useCallback(() => {
        if (!socket) return;
        const current = stateRef.current.call;
        if (current?.room_uuid) {
            socket.emit('call_joined', { room_uuid: current.room_uuid });
        }
    }, [socket]);

    const value = {
        status: state.status,
        call: state.call,
        aviso,
        startCall,
        acceptCall,
        rejectCall,
        cancelCall,
        leaveCall,
        reportJoined,
    };

    return (
        <CallContext.Provider value={value}>
            {children}
        </CallContext.Provider>
    );
};

export const useCall = () => {
    const ctx = useContext(CallContext);
    if (!ctx) throw new Error('useCall debe usarse dentro de CallProvider');
    return ctx;
};
